import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, TextField, Stack, MenuItem, InputAdornment} from 'views/components';
import {Translate, translate} from 'views/module';
import {Email, Phone} from 'views/icons';

const ContactSection = (props) => {
  const {onContactChange, contactData = {}, setValidity} = props;

  // Canadian provinces and territories
  const provinces = [
    {value: 'AB', label: translate('caregiverInterview.Contact.Provinces.AB')},
    {value: 'BC', label: translate('caregiverInterview.Contact.Provinces.BC')},
    {value: 'MB', label: translate('caregiverInterview.Contact.Provinces.MB')},
    {value: 'NB', label: translate('caregiverInterview.Contact.Provinces.NB')},
    {value: 'NL', label: translate('caregiverInterview.Contact.Provinces.NL')},
    {value: 'NS', label: translate('caregiverInterview.Contact.Provinces.NS')},
    {value: 'NT', label: translate('caregiverInterview.Contact.Provinces.NT')},
    {value: 'NU', label: translate('caregiverInterview.Contact.Provinces.NU')},
    {value: 'ON', label: translate('caregiverInterview.Contact.Provinces.ON')},
    {value: 'PE', label: translate('caregiverInterview.Contact.Provinces.PE')},
    {value: 'QC', label: translate('caregiverInterview.Contact.Provinces.QC')},
    {value: 'SK', label: translate('caregiverInterview.Contact.Provinces.SK')},
    {value: 'YT', label: translate('caregiverInterview.Contact.Provinces.YT')},
  ];

  // Update validity when contact data changes
  React.useEffect(() => {
    const isValid = Boolean(
        contactData.firstName?.trim() &&
        contactData.lastName?.trim() &&
        contactData.email?.trim() &&
        contactData.phone?.trim() &&
        contactData.streetAddress?.trim() &&
        contactData.city?.trim() &&
        contactData.province &&
        contactData.postalCode?.trim(),
    );
    setValidity(isValid);
  }, [contactData, setValidity]);

  // Handle input changes
  const handleFieldChange = (field, value) => {
    const updatedContact = {
      ...contactData,
      [field]: value,
    };

    if (onContactChange) {
      onContactChange(updatedContact);
    }
  };

  return (
    <Box
      sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}
    >
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverInterview.Contact.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverInterview.Contact.Subtitle'/>
      </Typography>

      <Stack spacing={3}>
        {/* Name Fields */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
              <Translate expression='caregiverInterview.Contact.FirstNameLabel'/>
            </Typography>
            <TextField
              value={contactData.firstName || ''}
              onChange={(text) => handleFieldChange('firstName', text)}
              fullWidth
              required
              size="small"
              placeholder={translate('caregiverInterview.Contact.FirstNamePlaceholder')}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
              <Translate expression='caregiverInterview.Contact.LastNameLabel'/>
            </Typography>
            <TextField
              value={contactData.lastName || ''}
              onChange={(text) => handleFieldChange('lastName', text)}
              fullWidth
              required
              size="small"
              placeholder={translate('caregiverInterview.Contact.LastNamePlaceholder')}
            />
          </Box>
        </Box>

        {/* Email and Phone */}
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
            <Translate expression='caregiverInterview.Contact.EmailLabel'/>
          </Typography>
          <TextField
            type="email"
            value={contactData.email || ''}
            onChange={(text) => handleFieldChange('email', text)}
            fullWidth
            required
            size="small"
            placeholder={translate('caregiverInterview.Contact.EmailPlaceholder')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
            <Translate expression='caregiverInterview.Contact.PhoneLabel'/>
          </Typography>
          <TextField
            type="tel"
            value={contactData.phone || ''}
            onChange={(text) => handleFieldChange('phone', text)}
            fullWidth
            required
            size="small"
            placeholder={translate('caregiverInterview.Contact.PhonePlaceholder')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {/* Address Section */}
        <Stack spacing={2}>
          {/* Street Address */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
              <Translate expression='caregiverInterview.Contact.StreetAddressLabel'/>
            </Typography>
            <TextField
              value={contactData.streetAddress || ''}
              onChange={(text) => handleFieldChange('streetAddress', text)}
              fullWidth
              required
              size="small"
              placeholder={translate('caregiverInterview.Contact.AddressPlaceholder')}
            />
          </Box>

          {/* City, Province or Territory, and Postal Code */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: '3fr 2fr 2fr'},
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
                <Translate expression='caregiverInterview.Contact.CityLabel'/>
              </Typography>
              <TextField
                value={contactData.city || ''}
                onChange={(text) => handleFieldChange('city', text)}
                fullWidth
                required
                size="small"
                placeholder={translate('caregiverInterview.Contact.CityPlaceholder')}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
                <Translate expression='caregiverInterview.Contact.ProvinceLabel'/>
              </Typography>
              <TextField
                select
                value={contactData.province || ''}
                onChange={(text) => handleFieldChange('province', text)}
                fullWidth
                required
                size="small"
              >
                {
                  provinces.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                }
              </TextField>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600, color: 'text.primary'}}>
                <Translate expression='caregiverInterview.Contact.PostalCodeLabel'/>
              </Typography>
              <TextField
                value={contactData.postalCode || ''}
                onChange={(text) => handleFieldChange('postalCode', text.toUpperCase())}
                required
                size="small"
                placeholder={translate('caregiverInterview.Contact.PostalCodePlaceholder')}
                inputProps={{
                  style: {textTransform: 'uppercase'},
                  maxLength: 7,
                }}
              />
            </Box>
          </Box>
        </Stack>
      </Stack>

      {/* Summary Section */}
      {
        Object.keys(contactData).length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              <Translate expression='caregiverInterview.Contact.Summary.Title'/>
            </Typography>
            <Box sx={{display: 'grid', gap: 1}}>
              {
                contactData.firstName && contactData.lastName && (
                  <Typography variant="body2">
                    <strong><Translate expression='caregiverInterview.Contact.Summary.Name'/></strong> {contactData.firstName} {contactData.lastName}
                  </Typography>
                )
              }
              {
                contactData.email && (
                  <Typography variant="body2">
                    <strong><Translate expression='caregiverInterview.Contact.Summary.Email'/></strong> {contactData.email}
                  </Typography>
                )
              }
              {
                contactData.phone && (
                  <Typography variant="body2">
                    <strong><Translate expression='caregiverInterview.Contact.Summary.Phone'/></strong> {contactData.phone}
                  </Typography>
                )
              }
              {
                (contactData.streetAddress || contactData.city || contactData.province || contactData.postalCode) && (
                  <Typography variant="body2">
                    <strong><Translate expression='caregiverInterview.Contact.Summary.Address'/></strong>{' '}
                    {
                      [
                        contactData.streetAddress,
                        contactData.city && contactData.province ?
                        `${contactData.city}, ${contactData.province}` :
                        contactData.city || contactData.province,
                        contactData.postalCode,
                      ]
                          .filter(Boolean)
                          .join(', ')
                    }
                  </Typography>
                )
              }
            </Box>
          </Box>
        )
      }
    </Box>
  );
};

ContactSection.propTypes = {
  onContactChange: PropTypes.func,
  contactData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    streetAddress: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    postalCode: PropTypes.string,
  }),
  setValidity: PropTypes.func,
};

export default ContactSection;
